import { Box, Flex, Text } from '@chakra-ui/react';
import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { AddressHighlightProvider } from 'lib/contexts/addressHighlight';
import useIsMobile from 'lib/hooks/useIsMobile';
import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { isBrowser } from 'toolkit/utils/isBrowser';
import SocketNewItemsNotice from 'ui/shared/SocketNewItemsNotice';
import useNewTxsSocket from 'ui/txs/socket/useTxsSocketTypeAll';

import LatestTxsItem from './LatestTxsItem';
import LatestTxsItemMobile from './LatestTxsItemMobile';

const zetachainFeature = config.features.zetachain;

const EXPIRATION_MS = 36 * 60 * 60 * 1000; // 36 hours
const LS_KEY = 'manual_transactions';

// Classify a transaction as manual or system
function isManualTx(tx: any) {
  // Manual: contract creation or token transfer, or no method, from is not a contract
  return (
    tx.from &&
    tx.from.is_contract === false &&
    (
      (tx.to === null) ||
      (tx.to && tx.to.is_contract === false) ||
      !tx.method ||
      (tx.transaction_types && (
        tx.transaction_types.includes('contract_creation') ||
        tx.transaction_types.includes('token_transfer')
      ))
    )
  );
}

const LatestTxs = ({ filter = 'all' }: { filter?: string }) => {
  const isMobile = useIsMobile();
  const txsCount = isMobile ? 2 : 5;
  const { data, isPending } = useApiQuery('general:homepage_txs', {
    queryOptions: {
      refetchInterval: 8000,
    },
  });

  const [ persistentManualTxs, setPersistentManualTxs ] = useState<Array<any>>([]);

  // Load manual transactions from API
  const loadManualTxs = React.useCallback(async() => {
    try {
      // Use proxy path relative to current domain
      const res = await axios.get('/node-api/manual-all-transactions');

      if (!Array.isArray(res?.data)) return;

      // API returns: { id, data, created_at }
      const txs = res?.data?.map((item: any) => item.data);

      // Deduplicate by hash
      const uniqueTxs = Array.from(new Map(txs.map((tx: any) => [ tx.hash, tx ])).values());

      setPersistentManualTxs(uniqueTxs);
    } catch (e) {
      console.error('Failed to load manual transactions from API', e);
    }
  }, []);

  // Add new manual txs from polling and store via API
  useEffect(() => {
    if (!data || isPending) return;

    const newManualTxs = data.filter(isManualTx);
    if (newManualTxs.length === 0) return;

    newManualTxs.forEach((tx: any) => {
      // Use proxy path relative to current domain
      axios.post('/node-api/manual-transactions', { data: tx })
        .then(res => {
          console.log('Stored manual transaction', res.data);
          loadManualTxs();
        })
        .catch((e) =>
          console.error('Failed to store manual transaction', e),
        );
    });

  }, [ data, isPending, loadManualTxs ]);

  // Initial load of manual transactions
  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      loadManualTxs();
    }

    return () => {
      isMounted = false;
    };
  }, [ loadManualTxs ]);

  const { num, showErrorAlert } = useNewTxsSocket({ type: 'txs_home', isLoading: isPending });

  const filteredData = useMemo(() => {
    if (filter === 'manual') {
      return persistentManualTxs;
    }
    return data;
  }, [ data, persistentManualTxs, filter ]);

  if (filter !== 'manual' && isPending) {
    return (
      <Box mb={ 3 } textStyle="sm">
        { Array.from({ length: txsCount }).map((_, index) => (
          <Skeleton key={ index } loading height="60px" mb={ 2 } borderRadius="md"/>
        )) }
      </Box>
    );
  }

  if (filteredData && filteredData.length > 0) {
    const txsUrl = route({ pathname: `/txs`, query: zetachainFeature.isEnabled ? { tab: 'evm' } : undefined });
    return (
      <>
        <SocketNewItemsNotice borderBottomRadius={ 0 } url={ txsUrl } num={ num } showErrorAlert={ showErrorAlert } isLoading={ isPending }/>
        <Box mb={ 3 } display={{ base: 'block', lg: 'none' }} textStyle="sm">
          { filteredData.slice(0, txsCount).map((tx) => (
            <LatestTxsItemMobile
              key={ tx.hash }
              tx={ tx }
            />
          )) }
        </Box>
        <AddressHighlightProvider>
          <Box mb={ 3 } display={{ base: 'none', lg: 'block' }} textStyle="sm">
            { filteredData.slice(0, txsCount).map((tx) => (
              <LatestTxsItem
                key={ tx.hash }
                tx={ tx }
              />
            )) }
          </Box>
        </AddressHighlightProvider>
        <Flex justifyContent="center">
          <Link textStyle="sm" href={ txsUrl }>View all transactions</Link>
        </Flex>
      </>
    );
  }

  return <Text mt={ 4 }>No data. Please reload the page.</Text>;
};

export default LatestTxs;
